// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricCategory } from "@/components/metrics/types";
import { cn } from "@/lib/utils";

// Use predefined pill colors from CATEGORY_STYLES
const OPPORTUNITY_PILL_CLASSES = CATEGORY_STYLES.Opportunity.pillColor;
const ACCOUNT_PILL_CLASSES = CATEGORY_STYLES.Account.pillColor;

import { Search } from "lucide-react";
import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";
import {
  type AccountData,
  formatAccountSourceRecordIdsForCSV,
  formatAccountsForCSV,
} from "@/lib/account-data";
import { formatDate } from "@/lib/date-formatting";
import { formatCurrency } from "@/lib/metrics/format-currency";
import {
  formatOpportunitiesForCSV,
  getOpportunityData,
} from "@/lib/opportunity-display-utils";
import type { UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource } from "@/utils/formatters";
import ComparisonBadge from "./components/comparison-badge"; // <-- Import ComparisonBadge

// Define WIP metrics
export const DRILLDOWN_LIMIT = 5000;

export const WIP_METRICS: Array<keyof CampaignMetrics> = [];

// Define hidden metrics that should be fetched but not displayed
export const HIDDEN_METRICS: Array<keyof CampaignMetrics> = [
  "opportunities_influenced_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
  "progressed_opportunities_amount",
  "opportunities_influenced_won_amount",
  "sourced_opportunities_amount",
  "opportunities_activated_amount",
  "opportunities_activated_won_amount",
  "engagements_per_account",
  "engagements_per_deal_contact",
  "engagements_per_person",
  "days_since_first_engagement",
  "days_since_latest_engagement",
  "first_engagement_date",
  "last_engagement_date",
];

// Define default metric order
export const DEFAULT_METRIC_ORDER: Array<keyof CampaignMetrics> = [
  "accounts_engaged",
  "engagements_per_account",
  "total_engagements",
  "people_engaged",
  "engagements_per_person",
  "deal_contacts_engaged",
  "engagements_per_deal_contact",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "opportunities_activated",
  "opportunities_activated_amount",
  "opportunities_activated_won",
  "opportunities_activated_won_amount",
  "sourced_opportunities",
  "sourced_opportunities_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "progressed_opportunities",
  "progressed_opportunities_amount",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
  "first_engagement_date",
  "last_engagement_date",
  "campaign_active_days",
];

// Centralized metric configuration
export interface MetricConfig {
  title: string;
  tooltip?: string;
  category: MetricCategory;
  drilldownColumns?: UnifiedTableColumn[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
  drilldownType?: "table" | "chart";
  render?: (
    value: number | null,
    options: {
      metricKey: keyof CampaignMetrics;
      metrics?: CampaignMetrics;
      benchmarkMetrics?: CampaignMetrics;
      isBenchmark?: boolean;
    },
  ) => React.ReactNode;
  primaryKey: string[];
}

// Centralized interfaces
export interface PersonItem {
  first_name?: string;
  last_name?: string;
  email_address?: string;
  job_title?: string;
  relation_source?: string;
}

export interface EngagementPeriodItem {
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface TotalEngagementsItem {
  id?: string;
  engagement_date?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name?: string;
  account_source_record_id?: string;
}

// Define fields used for engagement period display
export const ENGAGEMENT_PERIOD_FIELDS = [
  "first_engagement_date",
  "last_engagement_date",
] as const;

export interface StageProgressionItem {
  stage_progression?: string;
  stage_at_engagement?: string;
}

// Unified function for rendering metrics with various display options
export function renderMetric(
  value: number | null,
  options?: {
    metricKey?: keyof CampaignMetrics;
    metrics?: CampaignMetrics;
    benchmarkMetrics?: CampaignMetrics;
    isBenchmark?: boolean;
    isCurrency?: boolean;
    amountMetricKey?: keyof CampaignMetrics;
    secondaryMetricKey?: keyof CampaignMetrics;
    secondaryValue?: number;
    secondaryValueLabel?: React.ReactNode;
  },
): React.ReactNode {
  // --- Primary Value Calculation ---
  const safeValue = value ?? 0;
  const formattedValue = options?.isCurrency
    ? safeValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : safeValue.toLocaleString("en-US");

  const primaryBenchmarkValue =
    !options?.isBenchmark && options?.metricKey && options?.benchmarkMetrics // Only relevant for badge in campaign view
      ? options.benchmarkMetrics[options.metricKey]
      : undefined;
  const primaryBenchmarkRawValue =
    typeof primaryBenchmarkValue === "number" ? primaryBenchmarkValue : null;

  // Calculate primary benchmark value for benchmark view
  const primaryBenchmarkForDisplayRaw =
    options?.isBenchmark && options?.metricKey && options?.benchmarkMetrics
      ? options.benchmarkMetrics[options.metricKey]
      : undefined;
  const formattedPrimaryBenchmarkValue =
    typeof primaryBenchmarkForDisplayRaw === "number"
      ? (primaryBenchmarkForDisplayRaw ?? 0).toLocaleString("en-US", {
          style: options?.isCurrency ? "currency" : "decimal",
          currency: "USD",
          maximumFractionDigits: options?.isCurrency ? 0 : 0, // Assuming primary benchmarks are also whole numbers or currency
        })
      : null;

  // --- Secondary Content Calculation ---
  let secondaryContentJsx: React.ReactNode = null;
  if (options) {
    // Check if options exist
    const secondaryKey = options.amountMetricKey || options.secondaryMetricKey;
    const secondaryValue = secondaryKey
      ? options.metrics?.[secondaryKey]
      : options.secondaryValue;

    const secondaryBenchmarkValue =
      secondaryKey && options.benchmarkMetrics
        ? options.benchmarkMetrics[secondaryKey]
        : undefined;
    const secondaryBenchmarkRawValue =
      typeof secondaryBenchmarkValue === "number"
        ? secondaryBenchmarkValue
        : null;

    const formattedSecondaryValue =
      typeof secondaryValue === "number"
        ? (secondaryValue ?? 0).toLocaleString("en-US", {
            style: options.amountMetricKey ? "currency" : "decimal",
            currency: "USD",
            maximumFractionDigits: options.amountMetricKey ? 0 : 1,
          })
        : null;

    const formattedSecondaryBenchmarkValue =
      typeof secondaryBenchmarkRawValue === "number"
        ? (secondaryBenchmarkRawValue ?? 0).toLocaleString("en-US", {
            style: options.amountMetricKey ? "currency" : "decimal",
            currency: "USD",
            maximumFractionDigits: options.amountMetricKey ? 0 : 1,
          })
        : null;

    // --- Conditional Secondary Content JSX based on isBenchmark ---

    // Benchmark View: Render the secondary benchmark value (or placeholder), styled like the secondary campaign value
    if (options.isBenchmark) {
      // Only render if the formatted value exists (is not null)
      if (formattedSecondaryBenchmarkValue) {
        secondaryContentJsx = (
          <span title={formattedSecondaryBenchmarkValue}>
            {formattedSecondaryBenchmarkValue}
          </span>
        );
      }
    }
    // Campaign View: Render secondary campaign value + label/badge
    else {
      // CASE 1: Custom Label (passed via secondaryValueLabel without secondaryValue/Key)
      if (
        options.secondaryValueLabel &&
        !secondaryKey &&
        options.secondaryValue === undefined
      ) {
        secondaryContentJsx = (
          <span
            className="font-normal whitespace-nowrap"
            title={
              typeof options.secondaryValueLabel === "string"
                ? options.secondaryValueLabel
                : undefined
            }
          >
            {options.secondaryValueLabel}
          </span>
        );
      }
      // CASE 2: Numeric Secondary Value (Amount or Ratio)
      else if (secondaryKey || typeof options.secondaryValue === "number") {
        // Avoid rendering if primary is 0 for ratios/amounts
        if (
          !(
            (options.secondaryMetricKey || options.amountMetricKey) &&
            (value === null || value === 0)
          )
        ) {
          // Create tooltip text for the full secondary metric
          const tooltipText =
            formattedSecondaryValue &&
            options.secondaryValueLabel &&
            typeof options.secondaryValueLabel === "string"
              ? `${formattedSecondaryValue}${options.secondaryValueLabel}`
              : undefined;

          secondaryContentJsx = (
            // No longer need justify-between as benchmark is handled above
            <span
              className="inline-flex items-center gap-1"
              title={tooltipText}
            >
              {/* Display current secondary value */}
              <span>{formattedSecondaryValue}</span>

              {/* Display label and comparison badge (uses secondaryBenchmarkRawValue internally) */}
              {options.secondaryValueLabel &&
                typeof options.secondaryValueLabel === "string" && (
                  <span className="font-normal whitespace-nowrap">
                    {options.secondaryValueLabel}
                  </span>
                )}
              {secondaryKey && (
                <div className="ml-1">
                  <ComparisonBadge
                    currentValue={
                      typeof secondaryValue === "number" ? secondaryValue : null
                    }
                    benchmarkValue={secondaryBenchmarkRawValue} // Badge still uses benchmark for comparison
                    metricKey={secondaryKey}
                    size="sm"
                  />
                </div>
              )}
            </span>
          );
        }
      }
    }
  }

  // --- Final JSX Structure --- (Conditional Primary Value)
  return (
    // Apply items-end alignment when rendering benchmark view
    <div className={`flex flex-col ${options?.isBenchmark ? "items-end" : ""}`}>
      {/* Row 1: Primary Value (Campaign or Benchmark) + Optional Badge */}
      <div className="relative flex items-start">
        {/* Show Benchmark Primary Value if isBenchmark is true */}
        {options?.isBenchmark && (
          <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
            {formattedPrimaryBenchmarkValue ?? "-"}
          </span>
        )}
        {/* Show Campaign Primary Value + Badge if isBenchmark is false */}
        {!options?.isBenchmark && (
          <>
            <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
              {formattedValue}
            </span>
            {options?.metricKey && (
              <div className="ml-2 mt-2">
                <ComparisonBadge
                  currentValue={value}
                  benchmarkValue={primaryBenchmarkRawValue} // Use original primary benchmark for badge
                  metricKey={options.metricKey}
                />
              </div>
            )}
          </>
        )}
      </div>
      {/* Row 2: Secondary Content (Campaign or Benchmark) */}
      <div className="min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50">
        {secondaryContentJsx}
      </div>
    </div>
  );
}

// Helper function to create consistent account link rendering
const renderAccountLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name: string;
    account_name: string;
    account_id: string;
  };

  if (!typedItem.account_id) {
    return "";
  }

  const displayName = typedItem.account_name || typedItem.name;

  // Create links for both explorer and milestone analysis
  const explorerUrl = `/accounts/${typedItem.account_id}/explorer`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-pill
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
            ACCOUNT_PILL_CLASSES,
          )}
        >
          {displayName}
        </a>
      </TooltipTrigger>
      <TooltipContent>
        <p>View this account in Account Explorer</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Helper function to create consistent opportunity link rendering
const renderOpportunityLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name: string;
    opportunity_name: string;
    opportunity_id: string;
    account_id: string;
  };

  if (!typedItem.opportunity_id) {
    return "";
  }

  const displayName = typedItem.opportunity_name || typedItem.name;
  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={`/accounts/${typedItem.account_id}/explorer?opportunityId=${typedItem.opportunity_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
          >
            <span>{displayName}</span>
            <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>View this opportunity in Account Explorer</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

// Define metric data types
export type MetricDataType = "integer" | "float" | "currency" | "date";

// Mapping of each metric to its data type
export const METRIC_DATA_TYPES: Record<keyof CampaignMetrics, MetricDataType> =
  {
    accounts_engaged: "integer",
    total_engagements: "integer",
    people_engaged: "integer",
    engagements_per_account: "float",
    opportunities_influenced: "integer",
    opportunities_influenced_amount: "currency",
    opportunities_influenced_in_cycle: "integer",
    opportunities_influenced_in_cycle_amount: "currency",
    opportunities_created_post_engagement: "integer",
    opportunities_created_post_engagement_amount: "currency",
    progressed_opportunities: "integer",
    progressed_opportunities_amount: "currency",
    deal_contacts_engaged: "integer",
    engagements_per_deal_contact: "float",
    accounts_reactivated: "integer",
    accounts_activated: "integer",
    campaign_active_days: "integer",
    engagements_per_person: "float",
    opportunities_influenced_won: "integer",
    opportunities_influenced_won_amount: "currency",
    sourced_opportunities_amount: "currency",
    sourced_opportunities: "integer",
    days_since_first_engagement: "integer",
    days_since_latest_engagement: "integer",
    first_engagement_date: "date",
    last_engagement_date: "date",
    accounts_without_opps: "integer",
    opportunities_activated: "integer",
    opportunities_activated_amount: "currency",
    opportunities_activated_won: "integer",
    opportunities_activated_won_amount: "currency",
  };

// Map each metric key to its configuration
export const METRIC_CONFIG: Record<keyof CampaignMetrics, MetricConfig> = {
  accounts_engaged: {
    title: "Accounts Engaged",
    tooltip: "Count of accounts where any person engaged with this campaign.",
    category: "Account",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        secondaryValue: options.metrics?.engagements_per_account ?? 0,
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_account === 1
            ? "engagement"
            : "engagements"
        }/account`,
        secondaryMetricKey: "engagements_per_account", // <-- Pass secondary key
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  total_engagements: {
    title: "Total Engagements",
    tooltip:
      "Count of total engagements for this campaign (may include more than one per person). Excludes any engagements from your own team.",
    category: "Touchpoint",
    primaryKey: ["touchpoint_id"],
    drilldownColumns: [
      {
        key: "engagement_date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as TotalEngagementsItem;
          return formatDate(typedItem.engagement_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as TotalEngagementsItem;
          return formatDate(typedItem.engagement_date);
        },
      },
      {
        key: "person",
        title: "Person",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <span>
              {typedItem.first_name && typedItem.last_name
                ? `${typedItem.first_name} ${typedItem.last_name}`
                : "Unknown"}
            </span>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email",
        sortable: true,
        filterable: false,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as links, comma-separated
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
    ],
    defaultSort: { key: "engagement_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  people_engaged: {
    title: "People Engaged",
    tooltip:
      "Count of people who engaged with this campaign. Excludes any people from your own team.",
    category: "Person",
    primaryKey: ["person_id"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email Address",
        exportOnly: true,
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        secondaryValue: options.metrics?.engagements_per_person ?? 0,
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_person === 1
            ? "engagement"
            : "engagements"
        }/person`,
        secondaryMetricKey: "engagements_per_person", // <-- Pass secondary key
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  engagements_per_account: {
    title: "Engagements per Account",
    tooltip:
      "Average number of engagements with this campaign per account (total engagements divided by number of accounts engaged).",
    category: "Account",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "account_name",
        title: "Account",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "Unique People",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagements_per_person",
        title: "Engagements per Person",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "engagements_per_person", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        secondaryValue: options.metrics?.engagements_per_deal_contact ?? 0,
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_deal_contact === 1
            ? "engagement"
            : "engagements"
        }/contact`,
        secondaryMetricKey: "engagements_per_deal_contact", // <-- Pass secondary key
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  opportunities_influenced: {
    title: "Opportunities Influenced",
    tooltip:
      "Count and total amount of opportunities influenced by this campaign.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  opportunities_influenced_amount: {
    title: "Sum of Amounts for All Opportunities Influenced",
    tooltip:
      "Sum of the total amount of all opportunities influenced by this campaign.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.opportunities_influenced_amount === "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  opportunities_influenced_in_cycle: {
    title: "Opportunities Influenced In-Cycle",
    tooltip:
      "Count and total amount of opportunities influenced by this campaign during their active cycle.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_in_cycle_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  opportunities_influenced_in_cycle_amount: {
    title: "Sum of Amounts for Opportunities Influenced In-Cycle",
    tooltip:
      "Sum of the total amount of opportunities influenced by this campaign during their active cycle.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.opportunities_influenced_in_cycle_amount ===
          "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  opportunities_created_post_engagement: {
    title: "Opportunities Created Post-Engagement",
    tooltip:
      "Count and total amount of opportunities that were created after campaign engagement.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "days_till_creation",
        title: "Days from Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const value = item as { days_till_creation?: number | null };
          return value.days_till_creation !== null &&
            value.days_till_creation !== undefined
            ? `${value.days_till_creation} days`
            : "N/A";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_created_post_engagement_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  opportunities_created_post_engagement_amount: {
    title: "Sum of Amounts for Opportunities Created Post-Engagement",
    tooltip:
      "Sum of the total amount of opportunities that were created after campaign engagement.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.opportunities_created_post_engagement_amount ===
          "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  progressed_opportunities: {
    title: "Progressed Opportunities",
    tooltip:
      "Count and total amount of opportunities that progressed after campaign engagement.",
    category: "Opportunity",
    primaryKey: ["name", "account_name"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as StageProgressionItem;
          if (!typedItem.stage_progression) return "N/A";

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={index > engagementStageIndex ? "font-bold" : ""}
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as StageProgressionItem;
          if (!typedItem.stage_progression) return "N/A";
          return typedItem.stage_progression;
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "N/A";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date: string };
          return formatDate(value.progressed_date);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "first_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_engagement_date: string;
            last_engagement_date: string;
          };
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "progressed_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "progressed_opportunities_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  progressed_opportunities_amount: {
    title: "Sum of Amounts for Progressed Opportunities",
    tooltip:
      "Sum of the total amount of opportunities that progressed after campaign engagement.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as StageProgressionItem;
          if (!typedItem.stage_progression) return "N/A";

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={index > engagementStageIndex ? "font-bold" : ""}
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as StageProgressionItem;
          if (!typedItem.stage_progression) return "N/A";
          return typedItem.stage_progression;
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "N/A";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date: string };
          return formatDate(value.progressed_date);
        },
      },
      {
        key: "engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "first_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.progressed_opportunities_amount === "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  deal_contacts_engaged: {
    title: "Deal Contacts Engaged",
    tooltip:
      "Count of people who engaged with this campaign and have been in the buying group for any opportunity. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    primaryKey: [
      "person_id",
      "account_id",
      "last_name",
      "first_name",
      "account_name",
    ],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "relation_source",
        title: "Relation Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <span>
              {typedItem.relation_source
                ? formatRelationSource(typedItem.relation_source)
                : ""}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as PersonItem;
          return typedItem.relation_source
            ? formatRelationSource(typedItem.relation_source)
            : "";
        },
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            opportunities_involved?: unknown;
            opportunity_ids?: unknown;
            opportunities?: Array<{
              id: string;
              name: string;
              account_id: string;
            }>;
            account_id?: string;
          };

          // Check for new JSON format first
          if (
            typedItem.opportunities &&
            Array.isArray(typedItem.opportunities)
          ) {
            if (typedItem.opportunities.length === 0) return "-";

            return (
              <div className="flex flex-wrap gap-1">
                {typedItem.opportunities.map((opportunity) => {
                  const {
                    id: oppId,
                    name,
                    account_id: accountId,
                  } = opportunity;
                  return (
                    <Tooltip key={oppId}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            );
          }

          // Fall back to legacy format
          const value = typedItem.opportunities_involved;
          if (!value) return "-";

          const { names: opportunityNames, ids: opportunityIds } =
            getOpportunityData(typedItem);
          const accountId = typedItem.account_id;

          return (
            <div className="flex flex-wrap gap-1">
              {opportunityNames.map((name, index) => {
                const oppId = opportunityIds[index];

                if (oppId && accountId) {
                  return (
                    <Tooltip key={oppId}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                // Fallback to non-clickable badge if no ID
                return (
                  <span
                    key={index}
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${CATEGORY_STYLES.Opportunity.color.replace(
                      " shadow-sm",
                      "",
                    )}`}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities_involved?: unknown;
            opportunities?: Array<{
              id: string;
              name: string;
              account_id: string;
            }>;
          };

          // Check for new JSON format first
          if (
            typedItem.opportunities &&
            Array.isArray(typedItem.opportunities)
          ) {
            return formatOpportunitiesForCSV(typedItem.opportunities);
          }

          // Fall back to legacy format
          return formatOpportunitiesForCSV(typedItem.opportunities_involved);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "opportunity_source_record_ids",
        title: "Source Opportunity IDs",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        secondaryValue: options.metrics?.engagements_per_deal_contact ?? 0,
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_deal_contact === 1
            ? "engagement"
            : "engagements"
        }/contact`,
        secondaryMetricKey: "engagements_per_deal_contact", // <-- Pass secondary key
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  engagements_per_deal_contact: {
    title: "Engagements per deal contact",
    tooltip:
      "Average number of engagements with this campaign per buying group person. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    primaryKey: ["person_id", "account_id"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities?: unknown;
          };
          return formatOpportunitiesForCSV(typedItem.opportunities);
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagements_per_opportunity",
        title: "Engagements per Opportunity",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "engagements_per_opportunity", direction: "desc" },
  },
  accounts_reactivated: {
    title: "Accounts reactivated",
    tooltip:
      "Count of accounts that had no engagement for 90 days prior to engagement with this campaign.",
    category: "Account",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_engagements?: number };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        sortKey: "unique_people",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { unique_people?: number };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "last_engagement_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  accounts_activated: {
    title: "Accounts Activated",
    tooltip:
      "Count of accounts that had no engagement at any time prior to engagement with this campaign.",
    category: "Account",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_engagements?: number };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        sortKey: "unique_people",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { unique_people?: number };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "last_engagement_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  campaign_active_days: {
    title: "Campaign active days",
    tooltip:
      "Number of days the campaign was active, comparing days since first engagement and days since latest engagement.",
    category: "Campaign",
    drilldownType: "chart",
    drilldownColumns: [
      {
        key: "date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "engagement_count",
        title: "Daily Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "Unique People",
        sortable: true,
        filterable: false,
      },
      {
        key: "cumulative_engagements",
        title: "Cumulative Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "cumulative_people",
        title: "Cumulative People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "date", direction: "asc" },
    primaryKey: ["date"],
    render: (
      _value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) => {
      // Calculate primary value and date range string
      const primaryValue = options.metrics?.campaign_active_days ?? null;
      const dateRangeStr =
        options.metrics?.first_engagement_date &&
        options.metrics?.last_engagement_date
          ? `${formatDate(
              options.metrics.first_engagement_date,
            )} - ${formatDate(options.metrics.last_engagement_date)}`
          : "No engagement dates";

      // Call renderMetric, passing date range as secondaryValueLabel
      // renderMetric will handle badge rendering based on isBenchmark
      return renderMetric(primaryValue, {
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
        secondaryValueLabel: dateRangeStr, // Pass the string here
      });
    },
  },
  engagements_per_person: {
    title: "Engagements per Person",
    tooltip:
      "Average number of engagements per person who engaged with this campaign. Excludes any people from your own team.",
    category: "Person",
    primaryKey: ["person_id"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  opportunities_influenced_won: {
    title: "Won Opportunities Influenced",
    tooltip:
      "Count and total amount of won opportunities influenced by this campaign.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_won_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  opportunities_influenced_won_amount: {
    title: "Sum of Amounts for Won Opportunities Influenced",
    tooltip:
      "Sum of the total amount of won opportunities influenced by this campaign.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.opportunities_influenced_won_amount === "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  sourced_opportunities_amount: {
    title: "Sum of Amounts for Sourced Opportunities (SFDC)",
    tooltip:
      "Sum of the total amount of opportunities in Salesforce where this campaign is designated as the Primary Campaign Source. Note: this metric may not be available on all dashboards and may differ from Upside-calculated values.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency:
          METRIC_DATA_TYPES.sourced_opportunities_amount === "currency",
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  sourced_opportunities: {
    title: "Sourced opportunities (SFDC)",
    tooltip:
      "Indicates the number and total value of opportunities in Salesforce where this campaign is designated as the Primary Campaign Source. Note: this metric may not be available on all dashboards and may differ from Upside-calculated values.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        tooltip:
          "Note, null engagement periods signifies that the campaign_id associated to this opportunity in SFDC actually had no upside connected touchpoints",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        amountMetricKey: "sourced_opportunities_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      }),
  },
  days_since_first_engagement: {
    title: "Days since first engagement",
    tooltip:
      "Days since this campaign first began generating engagements, compared current date. This is useful to understand if enough time has elapsed to assess campaign performance.",
    category: "Campaign",
    drilldownType: "chart",
    primaryKey: ["date"],
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) => (
      <div className="flex flex-col">
        {renderMetric(value, {
          metricKey: options.metricKey,
          benchmarkMetrics: options.benchmarkMetrics,
          isBenchmark: options.isBenchmark,
        })}
        {/* No secondary value, but maintain space */}
        <div className="min-h-[24px] text-base text-muted-foreground/50">
          {!options.isBenchmark && <span className="font-normal"> days</span>}
        </div>
      </div>
    ),
  },
  days_since_latest_engagement: {
    title: "Days since latest engagement",
    tooltip:
      "Days since this campaign most recently generated engagements, compared current date. This is useful to understand if the campaign is active.",
    category: "Campaign",
    drilldownType: "chart",
    primaryKey: ["date"],
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) => (
      <div className="flex flex-col">
        {renderMetric(value, {
          metricKey: options.metricKey,
          benchmarkMetrics: options.benchmarkMetrics,
          isBenchmark: options.isBenchmark,
        })}
        {/* No secondary value, but maintain space */}
        <div className="min-h-[24px] text-base text-muted-foreground/50">
          {!options.isBenchmark && <span className="font-normal"> days</span>}
        </div>
      </div>
    ),
  },
  first_engagement_date: {
    title: "First engagement date",
    category: "Campaign",
    primaryKey: ["date"],
    // Update signature to include all options
    render: (
      _value: number | null,
      options: {
        metricKey: keyof CampaignMetrics; // Though unused, keep for consistency
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) => {
      // Call renderMetric for potential badge display, passing null as value
      const renderedMetric = renderMetric(null, {
        metricKey: "first_engagement_date", // Hardcode key
        benchmarkMetrics: options.benchmarkMetrics, // Pass if available
        isBenchmark: options.isBenchmark,
      });
      const dateStr = options.metrics?.first_engagement_date
        ? formatDate(options.metrics.first_engagement_date)
        : "N/A";
      // Combine badge (if any) with date string
      return (
        <div className="flex items-baseline">
          {renderedMetric}{" "}
          {/* This will likely be null but handles structure */}
          <span className="font-bold ml-1">{dateStr}</span> {/* Display date */}
        </div>
      );
    },
  },
  last_engagement_date: {
    title: "Last engagement date",
    category: "Campaign",
    primaryKey: ["date"],
    // Update signature to include all options
    render: (
      _value: number | null,
      options: {
        metricKey: keyof CampaignMetrics; // Though unused, keep for consistency
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) => {
      // Call renderMetric for potential badge display, passing null as value
      const renderedMetric = renderMetric(null, {
        metricKey: "last_engagement_date", // Hardcode key
        benchmarkMetrics: options.benchmarkMetrics, // Pass if available
        isBenchmark: options.isBenchmark,
      });
      const dateStr = options.metrics?.last_engagement_date
        ? formatDate(options.metrics.last_engagement_date)
        : "N/A";
      // Combine badge (if any) with date string
      return (
        <div className="flex items-baseline">
          {renderedMetric}{" "}
          {/* This will likely be null but handles structure */}
          <span className="font-bold ml-1">{dateStr}</span> {/* Display date */}
        </div>
      );
    },
  },
  accounts_without_opps: {
    title: "Accounts Engaged Outside of Opportunity Cycle",
    tooltip:
      "Count of accounts where all engagements happened while no matching opportunity was open.",
    category: "Account",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "people_engaged",
        title: "People Engaged",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        // Pass down relevant options
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  opportunities_activated: {
    title: "Opportunities with activation milestone citation",
    tooltip:
      "Count of opportunities where AI analysis identified this campaign's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Won",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { is_won: boolean };
          return value.is_won ? "Yes" : "No";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_engagements",
        title: "Campaign Touches",
        sortable: true,
        filterable: false,
      },
      {
        key: "analysis_created_date",
        dataType: "date",
        title: "Analysis Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
      },
      {
        key: "summary",
        title: "Analysis Summary",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { summary: string };
          if (!typedItem.summary) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-muted-foreground truncate max-w-xs cursor-help">
                  {typedItem.summary.length > 50
                    ? `${typedItem.summary.substring(0, 50)}...`
                    : typedItem.summary}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p>{typedItem.summary}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency: false,
        amountMetricKey: "opportunities_activated_amount",
        metrics: options.metrics,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  opportunities_activated_amount: {
    title: "Sum of Amounts for All Opportunities Activated",
    tooltip:
      "Sum of the total amount of all opportunities where AI analysis identified this campaign's touchpoints as influential.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [],
  },
  opportunities_activated_won: {
    title: "Won opportunities with activation milestone citation",
    tooltip:
      "Count of won opportunities where AI analysis identified this campaign's touchpoints as influential.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Won",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { is_won: boolean };
          return value.is_won ? "Yes" : "No";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_engagements",
        title: "Campaign Touches",
        sortable: true,
        filterable: false,
      },
      {
        key: "analysis_created_date",
        dataType: "date",
        title: "Analysis Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
      },
      {
        key: "summary",
        title: "Analysis Summary",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { summary: string };
          if (!typedItem.summary) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-muted-foreground truncate max-w-xs cursor-help">
                  {typedItem.summary.length > 50
                    ? `${typedItem.summary.substring(0, 50)}...`
                    : typedItem.summary}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p>{typedItem.summary}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (
      value: number | null,
      options: {
        metricKey: keyof CampaignMetrics;
        metrics?: CampaignMetrics;
        benchmarkMetrics?: CampaignMetrics;
        isBenchmark?: boolean;
      },
    ) =>
      renderMetric(value, {
        isCurrency: false,
        amountMetricKey: "opportunities_activated_won_amount",
        metrics: options.metrics,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
        isBenchmark: options.isBenchmark,
      }),
  },
  opportunities_activated_won_amount: {
    title: "Sum of Amounts for Won Opportunities Activated",
    tooltip:
      "Sum of the total amount of won opportunities where AI analysis identified this campaign's touchpoints as influential.",
    category: "Opportunity",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [],
  },
};
